import type { BlobBackend, LocalAuditSession, SessionBackend } from './types';

/**
 * Minimal promise wrapper over IndexedDB — deliberately dependency-free.
 * Audit data must NEVER touch localStorage/sessionStorage (spec §8);
 * IndexedDB is the only sanctioned local store.
 */

const DB_NAME = 'ssp-auditplus';
// v2: attachmentBlobs store (Phase 3c) — photo/PDF bytes live outside the
// session record so answer saves never rewrite megabytes of binary data.
const DB_VERSION = 2;
const SESSIONS = 'auditSessions';
const BLOBS = 'attachmentBlobs';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(SESSIONS)) {
          db.createObjectStore(SESSIONS, { keyPath: 'localId' });
        }
        if (!db.objectStoreNames.contains(BLOBS)) {
          db.createObjectStore(BLOBS);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'));
      request.onblocked = () => reject(new Error('IndexedDB open blocked'));
    });
  }
  return dbPromise;
}

function requestToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

/** IndexedDB-backed session storage (the production backend). */
export const idbBackend: SessionBackend = {
  async get(localId) {
    const db = await openDb();
    const tx = db.transaction(SESSIONS, 'readonly');
    return requestToPromise(
      tx.objectStore(SESSIONS).get(localId) as IDBRequest<LocalAuditSession | undefined>,
    );
  },
  async put(session) {
    const db = await openDb();
    const tx = db.transaction(SESSIONS, 'readwrite');
    await requestToPromise(tx.objectStore(SESSIONS).put(session));
  },
  async update(localId, mutate) {
    const db = await openDb();
    return new Promise<LocalAuditSession | undefined>((resolve, reject) => {
      const tx = db.transaction(SESSIONS, 'readwrite');
      const store = tx.objectStore(SESSIONS);
      let result: LocalAuditSession | undefined;
      let failure: unknown;
      const read = store.get(localId) as IDBRequest<LocalAuditSession | undefined>;
      // Writing from the read's success handler keeps the transaction open,
      // so no other readwrite transaction on this store can interleave.
      read.onsuccess = () => {
        const current = read.result;
        if (!current) return; // nothing to write; the empty tx completes
        try {
          const next = mutate(current);
          if (next !== current) store.put(next);
          result = next;
        } catch (err) {
          failure = err;
          tx.abort();
        }
      };
      // Resolve on COMMIT, not request success: callers act on durable state.
      tx.oncomplete = () => resolve(result);
      tx.onabort = () =>
        reject(failure ?? tx.error ?? new Error('IndexedDB update aborted'));
    });
  },
  async delete(localId) {
    const db = await openDb();
    const tx = db.transaction(SESSIONS, 'readwrite');
    await requestToPromise(tx.objectStore(SESSIONS).delete(localId));
  },
  async list() {
    const db = await openDb();
    const tx = db.transaction(SESSIONS, 'readonly');
    return requestToPromise(
      tx.objectStore(SESSIONS).getAll() as IDBRequest<LocalAuditSession[]>,
    );
  },
};

/** IndexedDB-backed attachment blob storage (keyed by attachment id). */
export const idbBlobBackend: BlobBackend = {
  async get(id) {
    const db = await openDb();
    const tx = db.transaction(BLOBS, 'readonly');
    return requestToPromise(
      tx.objectStore(BLOBS).get(id) as IDBRequest<Blob | undefined>,
    );
  },
  async put(id, blob) {
    const db = await openDb();
    const tx = db.transaction(BLOBS, 'readwrite');
    await requestToPromise(tx.objectStore(BLOBS).put(blob, id));
  },
  async delete(id) {
    const db = await openDb();
    const tx = db.transaction(BLOBS, 'readwrite');
    await requestToPromise(tx.objectStore(BLOBS).delete(id));
  },
};

/** In-memory blob backend for unit tests. */
export function createMemoryBlobBackend(): BlobBackend {
  const store = new Map<string, Blob>();
  return {
    get: (id) => Promise.resolve(store.get(id)),
    put: (id, blob) => {
      store.set(id, blob);
      return Promise.resolve();
    },
    delete: (id) => {
      store.delete(id);
      return Promise.resolve();
    },
  };
}

/** In-memory backend for unit tests (and any non-browser context). */
export function createMemoryBackend(): SessionBackend {
  const store = new Map<string, LocalAuditSession>();
  return {
    get: (id) => Promise.resolve(store.get(id) && structuredClone(store.get(id)!)),
    put: (s) => {
      store.set(s.localId, structuredClone(s));
      return Promise.resolve();
    },
    // Read + write in one synchronous step — the same atomicity IndexedDB
    // gives a single readwrite transaction.
    update: (id, mutate) => {
      const current = store.get(id);
      if (!current) return Promise.resolve(undefined);
      try {
        const next = mutate(structuredClone(current));
        store.set(id, structuredClone(next));
        return Promise.resolve(structuredClone(next));
      } catch (err) {
        return Promise.reject(err);
      }
    },
    delete: (id) => {
      store.delete(id);
      return Promise.resolve();
    },
    list: () => Promise.resolve([...store.values()].map((s) => structuredClone(s))),
  };
}
